import React, { useCallback, useMemo } from 'react';
import { Route } from 'react-router-dom';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import {
	roles as userRolesSelector,
	hasAuthToken,
	isBasketAdmin as isBasketAdminSelector,
} from 'modules/auth/authSelectors';
import { selectors as routeSelectors } from 'modules/route';
import { useSuspended } from 'modules/oidc/useAuthenticated';

import { masterRoles } from 'utility/constants/roles';
import RestrictionSuspended from 'components/Restrictions/RestrictionSuspended';
import RestrictionUnauthorized from 'components/Restrictions/RestrictionUnauthorized';

const RoleRestrictedRoute = ({
	component: Component,
	whitelistedRoles = [],
	isBasketAdmin,
	isLoggedIn,
	userRoles,
	fetchUserDetails,
	allowUnauth = false,
	queryParams,
	path,
	name,
	...rest
}) => {
	const isSuspended = useSuspended();

	const whiteListedRoleKeys = whitelistedRoles.map((role) => role.key);
	const masterRoleKeys = masterRoles.map((role) => role.key);

	const hasProperRoles = useMemo(() => {
		return (
			userRoles &&
			(userRoles.some((role) => masterRoleKeys.includes(role)) ||
				userRoles.some((role) => whiteListedRoleKeys.includes(role)))
		);
	}, [masterRoleKeys, userRoles, whiteListedRoleKeys]);

	const getRenderComponent = useCallback(
		(props) => {
			if (isBasketAdmin || (allowUnauth && !isLoggedIn) || hasProperRoles)
				return <Component name={name} path={path} {...props} {...rest} />;

			if (isSuspended) {
				return <RestrictionSuspended sx={{ flexGrow: 1 }} />;
			}

			return <RestrictionUnauthorized sx={{ flexGrow: 1 }} />;
		},
		[
			isBasketAdmin,
			allowUnauth,
			isLoggedIn,
			hasProperRoles,
			rest,
			isSuspended,
			name,
			path,
			Component,
		],
	);

	return <Route path={path} render={getRenderComponent} />;
};

const mapStateToProps = (state) => ({
	userRoles: userRolesSelector(state),
	isBasketAdmin: isBasketAdminSelector(state),
	isLoggedIn: hasAuthToken(state),
	queryParams: routeSelectors.getQueryParams(state),
	allowUnauth: routeSelectors.getAllowUnauth(state),
});

RoleRestrictedRoute.propTypes = {
	component: PropTypes.elementType,
	path: PropTypes.string,
	name: PropTypes.string,
	whitelistedRoles: PropTypes.array,
	isLoggedIn: PropTypes.bool.isRequired,
	userRoles: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
	fetchUserDetails: PropTypes.func,
	allowUnauth: PropTypes.bool,
};

export default connect(mapStateToProps)(RoleRestrictedRoute);
